import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheck, FiPlay, FiLock, FiBook } = FiIcons;

const CourseSidebar = ({ 
  course, 
  modules, 
  currentLesson, 
  currentModule,
  isOpen, 
  onClose, 
  onLessonSelect, 
  progress,
  isLessonComplete 
}) => {
  const getModuleProgress = (module) => {
    const totalLessons = module.lessons?.length || 0;
    const completedLessons = module.lessons?.filter(lesson => 
      isLessonComplete(lesson.id)
    ).length || 0;
    
    return {
      total: totalLessons,
      completed: completedLessons,
      percentage: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0
    };
  };

  const sidebarContent = (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiBook} className="w-5 h-5 text-nutrition-600" />
            <h2 className="font-semibold text-gray-900 truncate">{course?.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Course Progress */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600 mb-2">Course Progress</div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div 
            className="bg-nutrition-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress?.percentage || 0}%` }}
          />
        </div>
        <div className="text-xs text-gray-500">
          {progress?.completed || 0} of {progress?.total || 0} lessons completed
        </div>
      </div>

      {/* Modules & Lessons */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2">
          {modules.map((module, moduleIndex) => {
            const moduleProgress = getModuleProgress(module);
            const isCurrentModule = currentModule?.id === module.id;
            
            return (
              <div key={module.id} className="mb-4">
                {/* Module Header */}
                <div className={`p-3 rounded-lg mb-2 ${
                  isCurrentModule ? 'bg-nutrition-50 border border-nutrition-200' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900 text-sm">
                      Module {moduleIndex + 1}: {module.title}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {moduleProgress.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-nutrition-600 h-1 rounded-full transition-all duration-300"
                      style={{ width: `${moduleProgress.percentage}%` }}
                    />
                  </div>
                </div>

                {/* Lessons */}
                <div className="space-y-1 ml-2">
                  {module.lessons?.map((lesson, lessonIndex) => {
                    const isCurrentLesson = currentLesson?.id === lesson.id;
                    const isCompleted = isLessonComplete(lesson.id);
                    
                    return (
                      <motion.button
                        key={lesson.id}
                        onClick={() => onLessonSelect(lesson, module)}
                        className={`w-full text-left p-3 rounded-md transition-colors ${
                          isCurrentLesson
                            ? 'bg-nutrition-100 border border-nutrition-300'
                            : 'hover:bg-gray-50'
                        }`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {isCompleted ? (
                              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                              </div>
                            ) : isCurrentLesson ? (
                              <div className="w-6 h-6 bg-nutrition-600 rounded-full flex items-center justify-center">
                                <SafeIcon icon={FiPlay} className="w-3 h-3 text-white" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {lessonIndex + 1}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              isCurrentLesson ? 'text-nutrition-900' : 'text-gray-900'
                            }`}>
                              {lesson.title}
                            </p>
                            {lesson.estimated_minutes > 0 && (
                              <p className="text-xs text-gray-500">
                                {lesson.estimated_minutes} min
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 h-screen sticky top-0">
        {sidebarContent}
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: -320 }}
            animate={{ x: 0 }}
            exit={{ x: -320 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="lg:hidden fixed left-0 top-0 w-80 h-screen z-50"
          >
            {sidebarContent}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CourseSidebar;